/**
 * @file guessNameFromEmail.ts
 * @brief A guest's name from their email, only when the email reads like one.
 *
 * @details
 * Webpanel copy of FRONTEND/src/screens/MeetingIntelligence/guessNameFromEmail.ts
 * (keep the two in step).
 * Used by ExternalFromSearch to pre-fill the Name field for an email with no
 * Verblizr account (DES 28-09-2026). Pure.
 *
 * @author DES
 * @date 30-09-2026
 */

/** Local parts that look like words but are mailboxes, not people. */
const ROLE_MAILBOXES = new Set([
  "info",
  "admin",
  "office",
  "contact",
  "hello",
  "support",
  "sales",
  "team",
  "mail",
  "enquiries",
  "accounts",
  "noreply",
  "reception",
]);

/**
 * "john.smith@x" → "John Smith", "sara_khan@x" → "Sara Khan", "daud@x" →
 * "Daud". Returns "" for anything that does not read like a name: digits,
 * other symbols, a part under 3 letters, or a role mailbox (DES 28-09-2026:
 * "kuch logon nay email random rakha hota hai").
 */
export function guessNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  if (!/^[A-Za-z]+([._-][A-Za-z]+)*$/.test(local)) return "";
  const parts = local.split(/[._-]/);
  if (parts.some((p) => p.length < 2) || parts.join("").length < 3) return "";
  if (ROLE_MAILBOXES.has(local.toLowerCase())) return "";
  return parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}

/**
 * "ahsan ellahi" → "Ahsan Ellahi" (DES 28-09-2026: "naam ka pehla harf bara
 * kar do, even the second and third name"). Only the first letter of each
 * word is raised; the rest stays as typed, so "McDonald" and "AI" survive.
 * Extra spaces collapse to one.
 */
export function toNameCase(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
